import { useNavigateWithReturnState } from 'utils/useNavigateWithReturnState';
import ListCreateButton from 'components/buttons/ListCreateButton';
import { ExpandMoreRounded as ExpandMoreRoundedIcon, FolderOutlined as FolderOutlinedIcon } from 'components/icons';
import { cn } from 'utils/cn';
import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton.tsx';
import { DraftChip } from 'components/drafts/DraftChip';
import { DraftRowMenu } from 'components/drafts/DraftRecordActions';
import { useWithDrafts } from 'components/drafts/useWithDrafts';
import Loader from 'components/Loader.tsx';
import SubRowTable from 'components/tables/SubRowTable';
import type { DataTableColumnDef } from 'components/tables/data-table';
import ColumnDefNestedTable from 'components/tables/list-table/ColumnDefNestedTable';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import HoursDisplay from 'components/util/HoursDisplay.tsx';
import MoneyDisplay from 'components/util/MoneyDisplay.tsx';
import { type ReactNode, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useLazyGetProjectsWithFiltersQuery, useRemoveMatterFromProjectMutation } from 'store/api/projects';
import { FeatureFlag } from 'types/features';
import { MatterListItem } from 'types/matters.ts';
import { ProjectListItemDto } from 'types/projects';
import { t } from 'utils/getTranslations';
import { logError } from 'utils/monitoring';
import { secondsToHours } from 'utils/time.ts';
import { useClassifiers } from 'utils/useClassifiers.ts';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import { getMatterTableColumns } from '../matters/MattersTableWithFilters.tsx';
import { getLinks } from '../matters/util.tsx';
import ProjectMatterActionButtons from './components/ProjectMatterActionButtons.tsx';
import { ProjectStatusChip } from './components/ProjectStatusChip.tsx';

type ProjectMatterListItem = MatterListItem & {
    projectId: number;
};

type StatsCell = { getValue: () => ProjectListItemDto['projectStats']; row: { original: ProjectListItemDto } };

// A draft has no time or invoices of its own yet.
const orDraftDash =
    (cell: (context: StatsCell) => ReactNode) =>
    (context: StatsCell): ReactNode =>
        context.row.original.isDraft ? '—' : cell(context);

const getProjectHref = (project: ProjectListItemDto) => `/projects/${project.id}`;

const ProjectsTableWithFilters = () => {
    const navigateWithReturn = useNavigateWithReturnState();
    const isPartyNumberUsageEnabled = useIsFeatureEnabled(FeatureFlag.PARTY_NUMBER_USAGE);
    const isNewDesign = useIsNewDesign();

    const [fetchProjects, { data, isFetching }] = useLazyGetProjectsWithFiltersQuery();
    const fetchData = useWithDrafts(fetchProjects);
    const [removeMatterFromProject] = useRemoveMatterFromProjectMutation();

    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['MATTER_STATE_TYPE'],
    });

    // These six columns measure perfectly flat at every resolution, so all weights are 1.
    const columns = useMemo<DataTableColumnDef<ProjectListItemDto>[]>(
        () => [
            {
                header: t('projects.list-page.table.project-status'),
                accessorKey: 'projectName',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <div className="flex flex-col items-start">
                            <div className="flex w-full items-start justify-between gap-1">
                                <Link
                                    className="entity-link text-fg hover:text-link w-fit font-semibold no-underline hover:underline"
                                    to={`/projects/${item.id}`}
                                >
                                    {item.projectName || t('new-entity.project')}
                                </Link>
                                {/* The list has no actions column, so a draft's menu sits by its name. */}
                                {item.isDraft ? (
                                    <DraftRowMenu draft={{ type: 'project', id: item.id }} className="size-7" />
                                ) : null}
                            </div>
                            <div className="flex flex-wrap gap-1">
                                {item.isDraft ? <DraftChip /> : null}
                                <ProjectStatusChip status={item.projectStats.status} />
                            </div>
                        </div>
                    );
                },
            },
            {
                header: t('projects.list-page.table.assigned-to'),
                accessorKey: 'responsibleEmployee',
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: ({ row }) => {
                    const item = row.original;
                    return (
                        <div className="flex flex-col">
                            {getLinks(
                                'employee',
                                [item.supervisedByEmployee, item.responsibleEmployee].filter((employee) => !!employee),
                                { separator: null }
                            )}
                        </div>
                    );
                },
            },
            {
                header: t('projects.list-page.table.uninv-hrs-total'),
                // Explicit id: the accessor is a function, so without it the managed layout would
                // key this column's persisted geometry on the TRANSLATED header text.
                id: 'uninvoicedHours',
                accessorFn: (row) => row.projectStats,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: orDraftDash(({ getValue }) => {
                    const value = getValue();
                    return (
                        <div className="flex flex-col">
                            <HoursDisplay value={secondsToHours(value.uninvoicedTimeInSeconds, 2)} />
                            <span className="text-body1 text-fg-secondary">
                                <HoursDisplay value={secondsToHours(value.totalBilledTimeInSeconds, 2)} />
                            </span>
                        </div>
                    );
                }),
            },
            {
                header: t('projects.list-page.table.uninv-amount'),
                // Explicit id: the accessor is a function, so without it the managed layout would
                // key this column's persisted geometry on the TRANSLATED header text.
                id: 'uninvoicedAmount',
                accessorFn: (row) => row.projectStats,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: orDraftDash(({ getValue }) => {
                    const value = getValue();
                    return (
                        <div className="flex flex-col">
                            <MoneyDisplay amount={value.uninvoicedCapCurrency} currency={value.capCurrencyTypeCode} />
                            <span className="text-body1 text-fg-secondary">
                                <MoneyDisplay
                                    amount={value.uninvoicedBaseCurrency}
                                    currency={value.baseCurrencyTypeCode}
                                />
                            </span>
                        </div>
                    );
                }),
            },
            {
                header: t('projects.list-page.table.cap-amount'),
                // Explicit id: the accessor is a function, so without it the managed layout would
                // key this column's persisted geometry on the TRANSLATED header text.
                id: 'capAmount',
                accessorFn: (row) => row.projectStats,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: orDraftDash(({ getValue }) => {
                    const value = getValue();
                    return (
                        <div className="flex flex-col">
                            <MoneyDisplay amount={value.capLimitCapCurrency} currency={value.capCurrencyTypeCode} />
                            <span className="text-body1 text-fg-secondary">
                                <MoneyDisplay
                                    amount={value.capLimitBaseCurrency}
                                    currency={value.baseCurrencyTypeCode}
                                />
                            </span>
                        </div>
                    );
                }),
            },
            {
                header: t('projects.list-page.table.total'),
                // Explicit id: the accessor is a function, so without it the managed layout would
                // key this column's persisted geometry on the TRANSLATED header text.
                id: 'total',
                accessorFn: (row) => row.projectStats,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 150,
                },
                cell: orDraftDash(({ getValue }) => {
                    const value = getValue();
                    return (
                        <div className="flex flex-col">
                            <MoneyDisplay amount={value.totalCapCurrency} currency={value.capCurrencyTypeCode} />
                            <span className="text-body1 text-fg-secondary">
                                <MoneyDisplay amount={value.totalBaseCurrency} currency={value.baseCurrencyTypeCode} />
                            </span>
                        </div>
                    );
                }),
            },
            {
                id: 'expander',
                enableSorting: false,
                meta: {
                    // 32px toggle button + the managed cell's 32px horizontal padding.
                    fixedPx: 64,
                },
                cell: ({ row }) => {
                    const { matters } = row.original;
                    const hasMembers = (matters?.length ?? 0) > 0;
                    return hasMembers ? (
                        <TableRowCallForActionButton
                            onClick={row.getToggleExpandedHandler()}
                            aria-label={t(row.getIsExpanded() ? 'collapse-row' : 'expand-row')}
                            shape="rounded"
                        >
                            <ExpandMoreRoundedIcon
                                className={cn(
                                    'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                    row.getIsExpanded() && '-scale-y-100'
                                )}
                            />
                        </TableRowCallForActionButton>
                    ) : null;
                },
            },
        ],
        []
    );

    const matterTableColumns = useMemo<DataTableColumnDef<ProjectMatterListItem>[]>(() => {
        const onMatterRemove = async (projectId: number, matterId: number): Promise<void> => {
            try {
                await removeMatterFromProject({ projectId, matterId }).unwrap();
            } catch (err) {
                console.error(err);
                logError(err);
            }
        };

        const tableColumns = getMatterTableColumns<ProjectMatterListItem>(
            classifiers,
            undefined,
            false,
            isPartyNumberUsageEnabled
        );

        // Replacing the "actions" cell with project actions. The service-agreement column is dropped:
        // these matters come off the project payload, whose mapper does not resolve the contract, so
        // the column would render empty on every row.
        return tableColumns
            .filter((col) => col.id !== 'serviceAgreementContract')
            .map((col) =>
                col.id === 'actions'
                    ? {
                          header: '',
                          id: 'actions',
                          enableGrouping: false,
                          enableSorting: false,
                          meta: {
                              width: 10,
                              // The kebab's 32px and the cell's 16px on each side, in the new design's sub-table;
                              // the classic sub-row table reads `width` alone.
                              fixedPx: 64,
                          },
                          cell: ({ cell }) => {
                              const { id: matterId, projectId } = cell.row.original;

                              return matterId ? (
                                  <ProjectMatterActionButtons
                                      matterId={matterId}
                                      onRemove={(matterId) => {
                                          void onMatterRemove(projectId, matterId);
                                      }}
                                  />
                              ) : null;
                          },
                      }
                    : col
            );
    }, [classifiers, removeMatterFromProject, isPartyNumberUsageEnabled]);

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [
                {
                    selector: 'projectMatters',
                    comperator: '=isEmpty=',
                    filters: [
                        {
                            tooltip: 'projects.list-page.quick-filters.has-matters-tooltip',
                            label: t('projects.list-page.quick-filters.has-matters'),
                            argument: 'false',
                        },
                    ],
                },
            ],
            filters: [],
            searchBarSelectors: [
                { selector: 'projectName' },
                { selector: 'description' },
                { selector: 'supervisedByEmployee.fullName' },
                { selector: 'responsibleEmployee.fullName' },
            ],
        }),
        []
    );

    const renderExpandedContent = useCallback(
        (item: ProjectListItemDto): JSX.Element => {
            const matters = item.matters?.map((matter) => ({ ...matter, projectId: item.id })) ?? [];
            return isNewDesign ? (
                <ColumnDefNestedTable<ProjectMatterListItem> columns={matterTableColumns} data={matters} />
            ) : (
                <SubRowTable<ProjectMatterListItem> columns={matterTableColumns} data={matters} />
            );
        },
        [matterTableColumns, isNewDesign]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<ProjectListItemDto>
            getRowHref={getProjectHref}
            columnConfigId="projects"
            columns={columns}
            data={data}
            renderExpandedContent={renderExpandedContent}
            isLoading={isFetching}
            singularTextId="projects.list-page.table.project-singular"
            pluralTextId="projects.list-page.table.project-plural"
            noEntriesId="projects.list-page.no-projects-found"
            noEntriesBodyId="projects.empty.body"
            noEntriesIcon={FolderOutlinedIcon}
            noEntriesAction={
                <ListCreateButton
                    permissions={['CREATE.PROJECTS']}
                    variant="contained"
                    color="secondary"
                    onClick={() => navigateWithReturn('/projects/new')}
                    labelId="projects.list-page.add-new"
                    newLabelId="new-entity.project"
                />
            }
            fetchData={fetchData}
            disableUrlState={false}
            filterConfig={filterConfig}
            rowsPerPage={50}
        />
    );
};

export default ProjectsTableWithFilters;
